import React, { useState } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Clock, User, ChevronDown, ChevronRight, Copy, Check, ArrowRight,
  X, ExternalLink, Lightbulb, Edit3,
} from "lucide-react";

const D2_OBJECTIONS = [
  { q: "$99 feels expensive for software", a: "Compare it to one month of unemployment, $5K-$15K of lost salary, vs $99 for AI that runs 24/7. The guarantee covers you. Worst case, you keep using it free past 30 days." },
  { q: "I can do referrals manually", a: "You can. Manually you get 5-10 a week. ChillRefer does 600 a month, every one personalized. That's 30x more shots, automatically." },
  { q: "Will the messages sound like me?", a: "Every message is drafted using the recruiter's last 2 LinkedIn posts. You can edit before send, full control. Nothing goes out without your approval." },
  { q: "Is this risky for my LinkedIn?", a: "We stay inside LinkedIn's safe limits, 20 connection requests a day is normal user behavior. We've run this on 1,500+ accounts cleanly." },
  { q: "What's the guarantee again?", a: "Hired in 90: 10 real interviews in 90 days, or we give you 1 full year of ChillRefer free until you land. ChillRefer-only plan: no guarantee, just cancel anytime if it's not working." },
  { q: "What about applications?", a: "That's exactly where Hired in 90 comes in. Real human team applies to 780 roles a month for the full 90 days. ChillRefer alone is automation only — no human applications. Want me to walk you through it?" },
  { q: "I want to think about it", a: "Totally fair. What's the specific thing, the price, the guarantee, or fitting it into your week? I can address whichever right now." },
];

const D2_SCRIPTS = {
  1: {
    title: "Greet + Promise",
    lines: [
      "\"Welcome {name}! Thanks for jumping on. I'll keep this to 20 minutes flat.\"",
      "\"The headline you see is exactly what you'll walk out with — minimum 3 referrals in 30 days.\"",
      "\"Even if recruiters have been ghosting you, even if you don't have hours every day to spend on LinkedIn.\"",
      "(Walk through the 3 promise items so they know the agenda.)",
    ],
    tips: [
      "Read the headline word-for-word. It IS the promise.",
      "Inputs pinned at top. Fill role/market as you confirm them.",
    ],
  },
  2: {
    title: "Qualify",
    lines: [
      "\"Quick rapid-fire so everything that follows is specific to you.\"",
      "\"How many applications are you sending per week right now?\"",
      "\"How many LinkedIn requests are you sending per week?\"",
      "\"And how long have you been searching?\"",
    ],
    tips: [
      "Fill the inputs above as they answer. Slide updates live.",
      "If months >= 3, that's your ammo for slide 12 (apps math) and slide 14 (two roads).",
    ],
  },
  3: {
    title: "Educate Referrals",
    lines: [
      "\"Three ways someone can refer you — internal portal, direct DM to a recruiter, or warm intro to the hiring manager.\"",
      "\"And here's the secret: people actually want to refer you. They get $2K-$10K bonuses, manager points, and network growth. It's not begging, it's an exchange.\"",
      "\"Make sense? Referrals are an exchange, not a favor.\" (Wait for nod.)",
    ],
    tips: [
      "Reframes referrals from \"asking favors\" to \"opportunity exchange.\"",
      "Bridge: \"So how do you do all this without spending 6 hours a day? Watch this.\"",
    ],
  },
  4: {
    title: "Reveal + Flow",
    lines: [
      "\"Imagine if every day, automatically, while you sleep, ChillRefer found the right job, found the right referrer, liked their last 2 posts, sent a personalized connection request, asked for a referral when accepted, and made it stupidly easy for them to refer you in 30 seconds.\"",
      "\"That's exactly what it does. Every day. Without you opening LinkedIn.\"",
      "(Walk the 7 nodes. Pause on step 3 (likes posts) and step 6 (Referral Kit).)",
      "\"Could you see this running for you 24/7 in the background?\" (Wait for nod.)",
    ],
    tips: [
      "Slow down on the \"imagine if\" paragraph. Movie-trailer pace.",
      "Their YES here is currency for the next slide.",
    ],
  },
  5: {
    title: "LIVE Demo CR",
    lines: [
      "\"Forget the slides. Click the button and let me show you the real thing.\"",
      "(Open chillrefer.com. Show: search recruiters, autopilot dashboard, send a live request, open a Referral Kit.)",
      "\"Now that you've seen it live — does this feel real to you?\" (Wait.)",
    ],
    tips: [
      "Spend 4-6 minutes here. Do NOT rush.",
      "Their YES unlocks the next slide.",
    ],
  },
  6: {
    title: "Future-Pace 30 Days",
    lines: [
      "\"Picture this, {name}. 30 days from today. Same you. Same {role} target.\"",
      "\"3 to 5 real referrals already in the system. Each one a 9× better shot than a cold application.\"",
      "\"That's not a fantasy — that's the math when ChillRefer fires every single day.\"",
      "(Pause. Let them sit in that future for 3 seconds.)",
    ],
    tips: [
      "Slow down. Drop your voice. This is the hopeful slide.",
      "If they smile or lean forward, you've sold the outcome.",
    ],
  },
  7: {
    title: "Close $99",
    lines: [
      "\"Look at this side by side. Manual would take you 180 hours a month. ChillRefer takes you 1.\"",
      "\"600 personalized outreaches vs the 20-40 you'd get manually.\"",
      "\"All for $99. Locked in today. Cancel anytime. Live in under an hour.\"",
    ],
    tips: [
      "Don't ask for the close yet. The Permission Upsell does that better.",
      "Just plant the $99 number so the contrast hits later.",
    ],
  },
  8: {
    title: "Permission Upsell",
    lines: [
      "\"{name}, before you decide on ChillRefer — totally optional…\"",
      "\"We also have a Done-For-You service where our team applies for you, runs ChillRefer in the background, and guarantees interviews.\"",
      "\"Mind if I take 5 minutes to walk you through it?\"",
      "\"Worst case, you skip and lock in $99. Best case, you save 5 months.\"",
    ],
    tips: [
      "Always ask permission. Don't push.",
      "If YES → next slide. If NO → click 'Skip, lock in $99' to jump to S17 close.",
      "Frame it as \"info, not pressure.\"",
    ],
  },
  9: {
    title: "Meet BJA",
    lines: [
      "\"This is BulkJobApply. Hired in 90 is our 90-day done-for-you program.\"",
      "(Walk the 4 pillars: real human team, resume rebuild, WhatsApp group, ChillRefer + ChillApply.)",
      "\"And the killer at the bottom — The Hired-in-90 Promise: 10 interviews in 90 days, or 1 year of ChillRefer free.\"",
      "(Don't pre-empt pricing. That's slide 15.)",
    ],
    tips: [
      "Lead with the logo and the program tag. Branding moment.",
      "Walk the 4 pillars briskly — 30 seconds total.",
      "End on the Promise. That's the hook.",
    ],
  },
  10: {
    title: "3 Channels Flow",
    lines: [
      "\"Three channels get someone interviews — applications, referrals, LinkedIn presence.\"",
      "\"Hired in 90 runs all three: BulkJobApply for applications, ChillRefer for networking, LinkedIn optimization for visibility.\"",
      "\"Most candidates run only one. That's why they wait 5+ months.\"",
      "\"All 3 firing for the same person = 3 months average to land.\"",
    ],
    tips: [
      "This is the operational slide — show how the DFY service covers everything.",
      "Walk left to right: BJA → ChillRefer → LinkedIn. Make the flow obvious.",
    ],
  },
  11: {
    title: "Show Live Proof",
    lines: [
      "\"Two real things, on real accounts. Pick what you want to see.\"",
      "\"Option A: real client Gmail with interview emails from Microsoft, Stripe, Amazon.\"",
      "\"Option B: ChillApply running live, auto-applying to LinkedIn Easy Apply jobs.\"",
    ],
    tips: [
      "If they doubt applications work -> open Gmail.",
      "If they doubt automation -> open ChillApply.",
      "Pre-set: keep a real client Gmail open in another tab BEFORE the call.",
    ],
  },
  12: {
    title: "Personalized Math",
    lines: [
      "\"Look at the math for you specifically. {monthly} apps a month for {months} months.\"",
      "\"That's roughly $8K of lost salary every month you stay in this state.\"",
      "\"BJA hits 780 a month. Different volume, different speed, different outcome.\"",
      "\"Across 1,500+ clients: 94% got interviews, 18 days to first one.\"",
    ],
    tips: [
      "Slide auto-fills from inputs above.",
      "End on the results strip — proves the numbers aren't theoretical.",
    ],
  },
  13: {
    title: "Proof Wall",
    lines: [
      "\"These aren't testimonials we wrote — real WhatsApp threads with real clients.\"",
      "\"{name}, click on the case study that sounds closest to your situation.\"",
      "(Click into a chat with them. Read it together.)",
      "\"Switch to Monthly Results to see the actual application reports we send clients each month.\"",
    ],
    tips: [
      "Whatever case they pick → that's what shows on the Skeptic slide (slide 16).",
      "Now we have 12 months of monthly results — scroll the row to find any month.",
      "If PDF doesn't load, click Retry. Backend is occasionally slow.",
    ],
  },
  14: {
    title: "Two Roads",
    lines: [
      "\"{name}, from here you have two roads.\"",
      "\"Road 1: stay where you are. Apps disappearing into the void at 11pm. Another 3-5 months. Savings dwindling. Bills don't slow down.\"",
      "\"Road 2: let our team take it from here. Day 1 you're up. Week 1 first interview emails. Day 90, hired or 1 year ChillRefer free.\"",
      "(Pause. Look at them. Don't sell.)",
      "\"Both are valid. One has a known cost. The other has a known timeline.\"",
    ],
    tips: [
      "This is the EMOTIONAL close. Don't rush. Don't argue.",
      "No pricing here. Just the binary outcome reality.",
      "Pause for 5 seconds after the last line. They'll usually self-close.",
    ],
  },
  15: {
    title: "Stack the Value",
    lines: [
      "\"Two options. ChillRefer at $99 a month — automation only, cancel anytime.\"",
      "\"Or Hired in 90 — look at everything that's bundled in. Real human team is $2,400 of value. Add the personal coach, the dashboard, the ChillRefer fallback, and you're at $5,988 in real value.\"",
      "\"You pay $1,499. Not $5,988. We priced it at $1,499 because we'd rather have 100 successful clients than 10 expensive ones.\"",
      "\"Two ways to pay — $750 + $750, or pay in full and save $200.\"",
      "\"Where do you want to start?\" (Stop talking.)",
    ],
    tips: [
      "Read the value stack out loud line by line. Each $ figure makes $1,499 feel smaller.",
      "Two payment options now (no 3-instalment).",
      "After they pick, the slide auto-flows.",
    ],
  },
  16: {
    title: "Crush Skeptic Doubt",
    lines: [
      "\"Before you decide — you might be exactly where {csName} was 3 weeks ago.\"",
      "(Show the split-card. Read the hesitation aloud.)",
      "\"Almost didn't sign up. Same doubts you have right now.\"",
      "(Read the result on the right. Show the real WhatsApp screenshot.)",
      "\"That's not stock. That's them. The only question is — what are you going to choose?\"",
    ],
    tips: [
      "USE ONLY IF THEY HESITATE after slide 15. Skip if they already picked.",
      "If multiple case studies were selected on slide 13, use the dropdown above the title to switch to whichever one resonated most.",
      "Default fallback: Aman (price-skeptic arc).",
    ],
  },
  17: {
    title: "Read Their Mind",
    lines: [
      "\"{name}, right now you're probably thinking one of these 4 things.\"",
      "\"Let me deal with each before you have to say it out loud.\"",
      "(Walk left to right, top to bottom. Read each thought, then read the truth.)",
      "(End on the takeaway: only 30 spots per market this month, we're at 22 in {market}.)",
      "\"If you're a fit, I want you in. If you're not ready, that's fine — there's no hard sell here.\"",
    ],
    tips: [
      "DON'T sell on this slide. You're showing them you already KNOW their objections — that builds massive trust.",
      "Pause after the takeaway. The scarcity sentence does more than 10 minutes of pitching.",
      "If they say \"OK I want in\" here, skip directly to S19 Close & Pay. You don't need the After-You-Pay slide.",
    ],
  },
  18: {
    title: "Show First Hour",
    lines: [
      "\"Quick — let me show you exactly what happens the second you tap pay.\"",
      "(Walk through the 4 mockups: secure checkout → WhatsApp DM → Dashboard → first interview email.)",
      "\"No 'we'll get back to you in 48 hours.' This is the real first hour.\"",
      "\"You can already see day 1 has motion — referrers queued, dashboard live, first interview email coming.\"",
    ],
    tips: [
      "Show this BEFORE asking for payment — it kills the \"what happens next?\" friction.",
      "If they bought $99, the WhatsApp mockup auto-shows DM-only (no group). Conditional already.",
      "60 seconds total max. Then go to Take Payment.",
    ],
  },
  19: {
    title: "Take Payment",
    lines: [
      "\"You've seen what happens after. Now let's lock it in.\"",
      "\"Sending the WhatsApp payment link now — 2 minutes.\"",
      "(Confirm the option in the right panel.)",
      "\"Welcome to the team.\"",
    ],
    tips: [
      "ChillRefer-only = direct WhatsApp DM support, no group.",
      "Hired in 90 = group goes live within 1 hour after payment.",
      "Hit 'Lock it in & finish' below to advance to Thank You.",
    ],
  },
  20: {
    title: "Wrap + PDF",
    lines: [
      "\"Quick recap before we close out. WhatsApp payment link is on its way.\"",
      "\"Want me to send a clean PDF summary right now? It has everything we discussed.\"",
      "(Click 'Download summary PDF' -> attach to WhatsApp -> hit End demo.)",
    ],
    tips: [
      "PDF auto-includes selected case studies + plan + lead details.",
      "End demo button finalizes the close, sets status to demo_done.",
    ],
  },
};

function fillScript(line, lead, state) {
  const apps = parseFloat(state.weeklyApps) || 10;
  const months = state.monthsSearching || lead.monthsSearching || "a few";
  const monthly = apps * 4;
  const callbacks = (monthly * 0.004).toFixed(1);
  // Dynamic case-study name for skeptic slide
  let csName = "Aman";
  try {
    const sel = state.selectedCaseStudies || [];
    if (sel.length > 0) {
      // Look up via global window.CASE_STUDIES if available, else hardcoded fallbacks
      const FALLBACK_NAMES = { 1: "Khyati", 2: "Ravali", 3: "Lahari", 4: "Sahithi", 5: "Manu", 6: "Soujanya", 7: "Sahith & Hima", 8: "Aman", 9: "Edil", 10: "Suvam", 11: "Gopal", 12: "Saloni", 13: "Avi", 14: "Ajith", 15: "Sumanth", 16: "Preeti" };
      csName = FALLBACK_NAMES[sel[0]] || csName;
    }
  } catch (e) { /* keep default */ }
  return line
    .replace(/\{name\}/g, lead.clientName || "there")
    .replace(/\{role\}/g, state.confirmedRole || lead.targetRole || "your role")
    .replace(/\{market\}/g, state.confirmedMarket || lead.targetMarket || "your market")
    .replace(/\{apps\}/g, apps)
    .replace(/\{monthly\}/g, monthly)
    .replace(/\{callbacks\}/g, callbacks)
    .replace(/\{months\}/g, months)
    .replace(/\{csName\}/g, csName);
}

export default function CloserSidebar2({ state, actions, elapsed, closerTitles, popoutMode, onPopout }) {
  const { lead, currentScreen, selectedClose,
    confirmedRole, confirmedMarket, weeklyApps, linkedInConnections, monthsSearching } = state;
  const [navOpen, setNavOpen] = useState(false);
  const [objOpen, setObjOpen] = useState(false);
  const [activeObj, setActiveObj] = useState(null);
  const script = D2_SCRIPTS[currentScreen];

  return (
    <div className="closer-sidebar closer-sidebar-pinned" data-testid="closer-sidebar-d2">
      {/* PINNED TOP — always visible, no scroll */}
      <div className="d2-sidebar-pinned-top">
        <div className="sidebar-header-top">
          <span className="sidebar-timer"><Clock className="w-3.5 h-3.5" /> {elapsed}</span>
          <span className="d2-sidebar-screen-pill">
            {currentScreen}/20 · {script?.title || ""}
          </span>
          <div className="sidebar-header-btns">
            {!popoutMode && onPopout && (
              <Button size="sm" variant="outline" onClick={onPopout} data-testid="popout-btn-d2" className="sidebar-popout-btn">
                <ExternalLink className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={actions.endDemo} data-testid="end-demo-btn-d2" className="sidebar-end-btn">
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        <div className="d2-pinned-lead">
          <User className="w-4 h-4" />
          <div className="d2-pinned-lead-info">
            <div className="d2-pinned-lead-name">{lead.clientName} {lead.clientLastName}</div>
            <div className="d2-pinned-lead-meta">{fmtVal(confirmedRole || lead.targetRole)} · {fmtVal(confirmedMarket || lead.targetMarket)}</div>
          </div>
        </div>

        {/* Always-visible quick inputs grid (3 cols) */}
        <div className="d2-pinned-inputs">
          <PinnedInput label="Role"   value={confirmedRole}   onChange={actions.setConfirmedRole} testid="ctrl-role-d2" />
          <PinnedInput label="Market" value={confirmedMarket} onChange={actions.setConfirmedMarket} testid="ctrl-market-d2" />
          <PinnedInput label="Apps/wk" value={weeklyApps} onChange={actions.setWeeklyApps} type="number" testid="ctrl-weekly-d2" />
          <PinnedInput label="LI/wk"  value={linkedInConnections} onChange={actions.setLinkedInConnections} type="number" testid="ctrl-li-conn-d2" />
          <PinnedInput label="Months" value={monthsSearching} onChange={actions.setMonthsSearching} type="number" testid="ctrl-months-d2" />
        </div>

        {/* Nav bar — collapsible chip strip */}
        <div className="d2-pinned-navbar">
          <button className="d2-pinned-nav-btn" onClick={() => actions.setCurrentScreen(Math.max(1, currentScreen - 1))} disabled={currentScreen <= 1}>
            <ChevronRight className="w-3.5 h-3.5" style={{ transform: "rotate(180deg)" }} />
          </button>
          <button className="d2-pinned-nav-jump" onClick={() => setNavOpen(!navOpen)} data-testid="d2-nav-jump-toggle">
            Jump <ChevronDown className={`w-3.5 h-3.5 ${navOpen ? "rotate-180" : ""}`} />
          </button>
          <button className="d2-pinned-nav-btn" onClick={() => actions.setCurrentScreen(Math.min(20, currentScreen + 1))} disabled={currentScreen >= 20}>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {navOpen && (
          <div className="d2-pinned-nav-list" data-testid="d2-pinned-nav-list">
            {closerTitles.map((title, i) => (
              <button
                key={i}
                className={`d2-pinned-nav-item ${i + 1 === currentScreen ? "d2-pinned-nav-active" : ""} ${i + 1 < currentScreen ? "d2-pinned-nav-done" : ""}`}
                onClick={() => { actions.setCurrentScreen(i + 1); setNavOpen(false); }}
                data-testid={`nav-step-d2-${i + 1}`}
              >
                <span className="d2-pinned-nav-num">{i + 1}</span>
                <span>{title}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* SCROLLABLE BOTTOM — script + tips + objections */}
      <div className="d2-sidebar-scroll">
        {script && (
          <div className="screen-ctrl" data-testid={`screen-${currentScreen}-controls-d2`}>
            <div className="ctrl-script">
              {script.lines.map((line, i) => (
                <p key={i} className="ctrl-script-line">{fillScript(line, lead, state)}</p>
              ))}
            </div>
            {script.tips?.length > 0 && (
              <div className="d2-tips-block">
                <div className="d2-tips-head"><Lightbulb className="w-3.5 h-3.5" /> Tips</div>
                <ul className="ctrl-tips">
                  {script.tips.map((tip, i) => <li key={i}>{tip}</li>)}
                </ul>
              </div>
            )}

            {currentScreen === 17 && (
              <Button onClick={() => actions.setCurrentScreen(18)} className="d2-close-pay-btn" style={{ width: "100%", marginTop: 12, background: "#0ea5e9" }} data-testid="advance-objection-d2">
                <ArrowRight className="w-4 h-4 mr-1" /> No more doubts — show me what happens
              </Button>
            )}
            {currentScreen === 18 && (
              <Button onClick={() => actions.setCurrentScreen(19)} className="d2-close-pay-btn" style={{ width: "100%", marginTop: 12, background: "#7F77DD" }} data-testid="advance-to-close-d2">
                <ArrowRight className="w-4 h-4 mr-1" /> OK, let's lock it in
              </Button>
            )}
            {currentScreen === 19 && selectedClose && (
              <Button onClick={() => actions.setCurrentScreen(20)} className="d2-close-pay-btn" style={{ width: "100%", marginTop: 12 }} data-testid="advance-to-thanks-d2">
                <Check className="w-4 h-4 mr-1" /> Lock it in & finish
              </Button>
            )}
            {currentScreen === 20 && (
              <Button onClick={actions.endDemo} className="d2-close-pay-btn" style={{ width: "100%", marginTop: 12, background: "#1D9E75" }} data-testid="confirm-close-d2">
                <Check className="w-4 h-4 mr-1" /> Confirm close & end demo
              </Button>
            )}
          </div>
        )}

        <div className="sidebar-section">
          <button className="sidebar-section-toggle" onClick={() => setObjOpen(!objOpen)} data-testid="objections-toggle-d2">
            <span>Objection handling ({D2_OBJECTIONS.length})</span>
            {objOpen ? <ChevronDown className="w-4 h-4 ml-auto" /> : <ChevronRight className="w-4 h-4 ml-auto" />}
          </button>
          {objOpen && (
            <div className="objections-list" data-testid="objections-list-d2">
              {D2_OBJECTIONS.map((obj, i) => (
                <div key={i} className="objection-item">
                  <button className="objection-q" onClick={() => setActiveObj(activeObj === i ? null : i)} data-testid={`obj-q-d2-${i}`}>
                    {obj.q}
                  </button>
                  {activeObj === i && (
                    <div className="objection-a" data-testid={`obj-a-d2-${i}`}>
                      <p>{obj.a}</p>
                      <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(obj.a)} className="objection-copy-btn">
                        <Copy className="w-3 h-3" /> Copy
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PinnedInput({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="d2-pinned-input">
      <span>{label}</span>
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </label>
  );
}

function fmtVal(v) {
  return v && String(v).trim() ? v : "—";
}
